import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import coursesService, { FullCourseData, Segment } from '../../services/coursesService';
import { learningService } from '../../services/learningService';
import { getSegmentTranslation } from '../../utils/getTranslation';
import './ModelComparePage.css';

const COURSE_ID = '180d4d29-3c19-42f8-b608-ecd9754fbf06';

interface ModelResult {
  model: string;
  modelId: string;
  score: number | null;
  isCorrect: boolean | null;
  errors: Array<{
    type: 'vocabulary' | 'grammar' | 'structure';
    description: string;
    relatedWord?: string;
    relatedGrammar?: string;
  }>;
  feedback: string;
  latencyMs: number;
  error?: string;
}

interface SentenceData {
  id: string;
  index: number;
  text: string;
  translation: string;
  vocabulary?: Array<{ word: string; definition: string }>;
  grammarPoints?: Array<{ title: string; explanation: string }>;
}

interface SentenceState {
  userTranslation: string;
  results: ModelResult[] | null;
  loading: boolean;
}

const ModelComparePage: React.FC = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'zh-TW';

  const [courseData, setCourseData] = useState<FullCourseData | null>(null);
  const [sentences, setSentences] = useState<SentenceData[]>([]);
  const [sentenceStates, setSentenceStates] = useState<Record<number, SentenceState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testAllRunning, setTestAllRunning] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState(locale);

  // Flatten segments from course data
  const flattenSegments = useCallback((data: FullCourseData): SentenceData[] => {
    const result: SentenceData[] = [];
    let globalIndex = 0;

    // Try annotated sentences first (richer data)
    if (data.sentences && data.sentences.length > 0) {
      for (const s of data.sentences) {
        result.push({
          id: `sentence-${globalIndex}`,
          index: globalIndex,
          text: s.text || '',
          translation: getSegmentTranslation(
            { translation: s.translation, translations: s.translations },
            selectedLocale,
          ),
          vocabulary: undefined,
          grammarPoints: s.grammar_points?.map((g) => ({
            title: g.explanation_en || '',
            explanation: g.explanation_native || '',
          })),
        });
        globalIndex++;
      }
    } else {
      // Fallback to section/episode/segment hierarchy
      for (const section of data.sections || []) {
        for (const episode of section.episodes || []) {
          for (const segment of episode.segments || []) {
            result.push({
              id: segment.id,
              index: globalIndex,
              text: segment.text,
              translation: getSegmentTranslation(segment, selectedLocale),
            });
            globalIndex++;
          }
        }
      }
    }

    return result;
  }, [selectedLocale]);

  // Load course data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    coursesService.getFullCourse(COURSE_ID)
      .then((data) => {
        if (cancelled) return;
        setCourseData(data);
        const sents = flattenSegments(data);
        setSentences(sents);
        // Initialize states
        const states: Record<number, SentenceState> = {};
        sents.forEach((_, i) => {
          states[i] = { userTranslation: '', results: null, loading: false };
        });
        setSentenceStates(states);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load course');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [flattenSegments]);

  // Build surrounding context (same logic as ComprehensionPage)
  const buildSurroundingContext = useCallback((idx: number): string => {
    const nearby: string[] = [];
    for (let i = Math.max(0, idx - 2); i <= Math.min(sentences.length - 1, idx + 2); i++) {
      if (i !== idx && sentences[i]?.text) {
        nearby.push(sentences[i].text);
      }
    }
    return [
      courseData?.course?.title ? `Title: ${courseData.course.title}` : '',
      ...nearby,
    ].filter(Boolean).join(' | ');
  }, [sentences, courseData]);

  // Test a single sentence
  const testSentence = useCallback(async (idx: number) => {
    const sentence = sentences[idx];
    const state = sentenceStates[idx];
    if (!sentence || !state?.userTranslation.trim()) return;

    setSentenceStates(prev => ({
      ...prev,
      [idx]: { ...prev[idx], loading: true, results: null },
    }));

    try {
      const context: any = {};
      if (sentence.vocabulary?.length) {
        context.vocabulary = sentence.vocabulary;
      }
      if (sentence.grammarPoints?.length) {
        context.grammarPoints = sentence.grammarPoints;
      }

      const surroundingContext = buildSurroundingContext(idx);

      const response = await learningService.compareModels({
        courseId: COURSE_ID,
        sentenceIndex: idx,
        originalSentence: sentence.text,
        correctTranslation: sentence.translation,
        userTranslation: state.userTranslation.trim(),
        locale: selectedLocale,
        ...(Object.keys(context).length > 0 ? { context } : {}),
        ...(surroundingContext ? { surroundingContext } : {}),
      });

      setSentenceStates(prev => ({
        ...prev,
        [idx]: { ...prev[idx], loading: false, results: response.results },
      }));
    } catch (err: any) {
      setSentenceStates(prev => ({
        ...prev,
        [idx]: {
          ...prev[idx],
          loading: false,
          results: [{
            model: 'Error',
            modelId: '',
            score: null,
            isCorrect: null,
            errors: [],
            feedback: '',
            latencyMs: 0,
            error: err?.message || 'Request failed',
          }],
        },
      }));
    }
  }, [sentences, sentenceStates, selectedLocale, buildSurroundingContext]);

  // Test all sentences sequentially
  const testAll = useCallback(async () => {
    setTestAllRunning(true);
    for (let i = 0; i < sentences.length; i++) {
      if (sentenceStates[i]?.userTranslation.trim()) {
        await testSentence(i);
      }
    }
    setTestAllRunning(false);
  }, [sentences, sentenceStates, testSentence]);

  // Calculate summary stats
  const summaryStats = React.useMemo(() => {
    const modelStats: Record<string, { totalScore: number; totalLatency: number; count: number }> = {};
    Object.values(sentenceStates).forEach(state => {
      if (!state.results) return;
      state.results.forEach(r => {
        if (r.score === null) return;
        if (!modelStats[r.model]) {
          modelStats[r.model] = { totalScore: 0, totalLatency: 0, count: 0 };
        }
        modelStats[r.model].totalScore += r.score;
        modelStats[r.model].totalLatency += r.latencyMs;
        modelStats[r.model].count++;
      });
    });
    return modelStats;
  }, [sentenceStates]);

  const getScoreColor = (score: number | null): string => {
    if (score === null) return '#999';
    if (score >= 90) return '#22c55e';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div className="model-compare-page">
        <div className="model-compare-loading">Loading course data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="model-compare-page">
        <div className="model-compare-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="model-compare-page">
      <div className="model-compare-header">
        <h1>Model A/B Comparison - Translation Evaluation</h1>
        <div className="model-compare-meta">
          <span>Course: {courseData?.course?.title || COURSE_ID}</span>
          <span>{sentences.length} sentences</span>
          <label>
            Locale:
            <select
              value={selectedLocale}
              onChange={e => setSelectedLocale(e.target.value)}
            >
              <option value="zh-TW">zh-TW</option>
              <option value="zh-CN">zh-CN</option>
              <option value="en">en</option>
              <option value="ko">ko</option>
            </select>
          </label>
        </div>
      </div>

      <div className="model-compare-sentences">
        {sentences.map((sentence, idx) => {
          const state = sentenceStates[idx] || { userTranslation: '', results: null, loading: false };
          return (
            <div key={sentence.id} className="model-compare-sentence">
              <div className="sentence-info">
                <div className="sentence-index">#{idx + 1}</div>
                <div className="sentence-text">{sentence.text}</div>
                <div className="sentence-reference">{sentence.translation}</div>
              </div>

              <div className="sentence-input-row">
                <input
                  type="text"
                  placeholder="Your translation..."
                  value={state.userTranslation}
                  onChange={e => setSentenceStates(prev => ({
                    ...prev,
                    [idx]: { ...prev[idx], userTranslation: e.target.value },
                  }))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') testSentence(idx);
                  }}
                  disabled={state.loading}
                />
                <button
                  onClick={() => testSentence(idx)}
                  disabled={state.loading || !state.userTranslation.trim()}
                  className="test-btn"
                >
                  {state.loading ? 'Testing...' : 'Test'}
                </button>
              </div>

              {state.results && (
                <div className="model-results-grid">
                  {state.results.map((r, rIdx) => (
                    <div key={rIdx} className={`model-result-card ${r.error ? 'error' : ''}`}>
                      <div className="model-result-header">
                        <span className="model-name">{r.model}</span>
                        <span className="model-latency">{r.latencyMs ? `${(r.latencyMs / 1000).toFixed(1)}s` : '-'}</span>
                      </div>
                      {r.error ? (
                        <div className="model-result-error">{r.error}</div>
                      ) : (
                        <>
                          <div
                            className="model-result-score"
                            style={{ color: getScoreColor(r.score) }}
                          >
                            {r.score ?? '-'}
                          </div>
                          <div className="model-result-feedback">{r.feedback}</div>
                          {r.errors.length > 0 && (
                            <div className="model-result-errors">
                              {r.errors.map((err, eIdx) => (
                                <div key={eIdx} className="error-item">
                                  <span className={`error-type error-type-${err.type}`}>{err.type}</span>
                                  <span className="error-desc">{err.description}</span>
                                  {err.relatedWord && (
                                    <span className="error-word">{err.relatedWord}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Section */}
      {Object.keys(summaryStats).length > 0 && (
        <div className="model-compare-summary">
          <h2>Summary</h2>
          <div className="summary-grid">
            {Object.entries(summaryStats).map(([model, stats]) => (
              <div key={model} className="summary-card">
                <div className="summary-model">{model}</div>
                <div className="summary-stat">
                  Avg Score: <strong style={{ color: getScoreColor(stats.totalScore / stats.count) }}>
                    {(stats.totalScore / stats.count).toFixed(1)}
                  </strong>
                </div>
                <div className="summary-stat">
                  Avg Latency: <strong>{(stats.totalLatency / stats.count / 1000).toFixed(1)}s</strong>
                </div>
                <div className="summary-stat">
                  Tested: <strong>{stats.count}</strong> sentences
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Test All Button */}
      <div className="model-compare-actions">
        <button
          onClick={testAll}
          disabled={testAllRunning}
          className="test-all-btn"
        >
          {testAllRunning ? 'Testing All...' : 'Test All Sentences'}
        </button>
      </div>
    </div>
  );
};

export default ModelComparePage;
